import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, Store, CheckCircle, ArrowRight, Shield, TrendingUp, MapPin, Tv, DollarSign, BarChart3, Target, Users, Megaphone, Calendar, Zap, Radio } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fadeInUp, staggerContainer, viewportConfig } from '@/lib/animations';

const governmentPainPoints = [
  { problem: "Complex procurement requirements", solution: "Simplified onboarding" },
  { problem: "Limited marketing budgets", solution: "Cost-effective geo-targeting" },
  { problem: "Compliance concerns", solution: "Transparent reporting & audit trails" },
  { problem: "Slow agency timelines", solution: "Rapid campaign deployment" },
];

const governmentUseCases = [
  "Public Health Campaigns",
  "Recruitment Drives",
  "Program Awareness",
  "Emergency Notifications",
  "Tourism Promotion",
  "Voter Outreach",
];

const businessPainPoints = [
  { problem: "TV ads seem unaffordable", solution: "Flexible budgets for any size" },
  { problem: "Wasted ad spend", solution: "Precision geo-targeting by ZIP code" },
  { problem: "No creative resources", solution: "Full-service ad production" },
  { problem: "Measuring ROI is hard", solution: "Real-time performance dashboards" },
];

const businessUseCases = [
  "Grand Openings",
  "Seasonal Promotions",
  "Service Area Expansion",
  "Brand Awareness",
  "Event Marketing",
  "Competitive Conquesting",
];

type TabType = 'government' | 'business';

export function AudienceTabs() {
  const [activeTab, setActiveTab] = useState<TabType>('government');

  return (
    <section id="solutions" className="py-12 sm:py-14 md:py-16 lg:py-20 bg-gray-50">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={viewportConfig}
        variants={staggerContainer}
        className="container mx-auto px-5 sm:px-6"
      >
        {/* Section Header */}
        <motion.div variants={fadeInUp} className="text-center mb-10 md:mb-14">
          <span className="text-teal font-semibold text-xs sm:text-sm tracking-widest uppercase mb-2 sm:mb-3 block">
            Who We Serve
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-navy-deep tracking-tight mb-4">
            One Platform. Two Audiences.{' '}
            <span className="text-teal">Unlimited Reach.</span>
          </h2>
          <p className="text-gray-700 text-base md:text-lg max-w-2xl mx-auto">
            Federal agencies and local businesses alike get the same powerful advertising technology.
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div variants={fadeInUp} className="flex justify-center mb-10">
          <div className="inline-flex bg-white rounded-full p-1.5 shadow-lg">
            <button
              onClick={() => setActiveTab('government')}
              className={`flex items-center gap-2 px-5 sm:px-8 py-3 rounded-full text-sm font-semibold transition-all duration-300 ${
                activeTab === 'government'
                  ? 'bg-navy-deep text-white shadow-md'
                  : 'text-gray-600 hover:text-navy-deep'
              }`}
            >
              <Building2 size={18} />
              <span className="hidden sm:inline">Government Agencies</span>
              <span className="sm:hidden">Government</span>
            </button>
            <button
              onClick={() => setActiveTab('business')}
              className={`flex items-center gap-2 px-5 sm:px-8 py-3 rounded-full text-sm font-semibold transition-all duration-300 ${
                activeTab === 'business'
                  ? 'bg-navy-deep text-white shadow-md'
                  : 'text-gray-600 hover:text-navy-deep'
              }`}
            >
              <Store size={18} />
              <span className="hidden sm:inline">Small & Mid-Size Businesses</span>
              <span className="sm:hidden">Business</span>
            </button>
          </div>
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'government' ? (
            <motion.div
              key="government"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-center"
            >
              {/* Left Content - 60% */}
              <div className="lg:col-span-3 space-y-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-teal/10 rounded-xl">
                    <Shield className="w-6 h-6 text-teal" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-navy-deep">
                    Mission-Driven Campaigns for Public Sector
                  </h3>
                </div>
                
                <p className="text-gray-700 text-base leading-relaxed">
                  Reach constituents, promote programs, and drive civic engagement with compliant, transparent advertising that meets government standards.
                </p>

                {/* Pain Points */}
                <div className="space-y-3">
                  {governmentPainPoints.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                      <CheckCircle className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-gray-600 line-through text-sm">{item.problem}</span>
                        <span className="text-navy-deep font-medium text-sm ml-2">→ {item.solution}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Use Cases */}
                <div className="flex flex-wrap justify-center lg:justify-start gap-2 pt-2">
                  {governmentUseCases.map((useCase, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1.5 bg-teal text-white text-xs font-semibold rounded-full"
                    >
                      {useCase}
                    </span>
                  ))}
                </div>

                <div className="flex justify-center lg:justify-start">
                  <Button
                    asChild
                    className="bg-orange hover:bg-orange-hover text-white font-semibold mt-4"
                  >
                    <a href="#contact">
                      See Government Solutions
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </a>
                  </Button>
                </div>
              </div>

              {/* Right Visual - 40% */}
              <div className="lg:col-span-2 relative">
                <div className="bg-gradient-to-br from-navy-deep to-navy rounded-2xl p-8 aspect-square flex items-center justify-center">
                  <div className="relative w-full h-full">
                    {/* US Map visualization */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <motion.div
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 3, repeat: Infinity }}
                        className="w-32 h-32 rounded-full bg-teal/20 flex items-center justify-center"
                      >
                        <motion.div
                          animate={{ scale: [1, 1.15, 1] }}
                          transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                          className="w-20 h-20 rounded-full bg-teal/30 flex items-center justify-center"
                        >
                          <Building2 className="w-10 h-10 text-teal-light" />
                        </motion.div>
                      </motion.div>
                    </div>
                    {/* Floating icons */}
                    <motion.div
                      animate={{ y: [-5, 5, -5] }}
                      transition={{ duration: 3, repeat: Infinity }}
                      className="absolute top-4 right-8"
                    >
                      <Tv className="w-6 h-6 text-white/50" />
                    </motion.div>
                    <motion.div
                      animate={{ y: [5, -5, 5] }}
                      transition={{ duration: 2.5, repeat: Infinity }}
                      className="absolute bottom-8 left-4"
                    >
                      <MapPin className="w-6 h-6 text-orange" />
                    </motion.div>
                    <motion.div
                      animate={{ y: [-3, 3, -3] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="absolute top-12 left-8"
                    >
                      <Radio className="w-5 h-5 text-white/40" />
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="business"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-center"
            >
              {/* Left Content - 60% */}
              <div className="lg:col-span-3 space-y-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-orange/10 rounded-xl">
                    <TrendingUp className="w-6 h-6 text-orange" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-navy-deep">
                    Big-Brand Reach on a Small Business Budget
                  </h3>
                </div>
                
                <p className="text-gray-700 text-base leading-relaxed">
                  Access the same premium broadcast networks as Fortune 500 companies without the Fortune 500 price tag.
                </p>

                {/* Pain Points */}
                <div className="space-y-3">
                  {businessPainPoints.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                      <CheckCircle className="w-5 h-5 text-orange flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-gray-600 line-through text-sm">{item.problem}</span>
                        <span className="text-navy-deep font-medium text-sm ml-2">→ {item.solution}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Use Cases */}
                <div className="flex flex-wrap justify-center lg:justify-start gap-2 pt-2">
                  {businessUseCases.map((useCase, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1.5 bg-orange text-white text-xs font-semibold rounded-full"
                    >
                      {useCase}
                    </span>
                  ))}
                </div>

                <div className="flex justify-center lg:justify-start">
                  <Button
                    asChild
                    className="bg-orange hover:bg-orange-hover text-white font-semibold mt-4"
                  >
                    <a href="#contact">
                      See Business Solutions
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </a>
                  </Button>
                </div>
              </div>

              {/* Right Visual - 40% */}
              <div className="lg:col-span-2 relative">
                <div className="bg-gradient-to-br from-orange/90 to-orange-hover rounded-2xl p-8 aspect-square flex items-center justify-center">
                  <div className="relative w-full h-full">
                    {/* Store/geo visualization */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <motion.div
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ duration: 3, repeat: Infinity }}
                        className="w-32 h-32 rounded-full border-2 border-white/30 flex items-center justify-center"
                      >
                        <motion.div
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}
                          className="w-20 h-20 rounded-full border-2 border-white/50 flex items-center justify-center"
                        >
                          <Store className="w-10 h-10 text-white" />
                        </motion.div>
                      </motion.div>
                    </div>
                    {/* Floating icons */}
                    <motion.div
                      animate={{ y: [-5, 5, -5] }}
                      transition={{ duration: 2.5, repeat: Infinity }}
                      className="absolute top-6 right-4"
                    >
                      <DollarSign className="w-6 h-6 text-white/60" />
                    </motion.div>
                    <motion.div
                      animate={{ y: [3, -3, 3] }}
                      transition={{ duration: 3, repeat: Infinity }}
                      className="absolute bottom-6 right-8"
                    >
                      <BarChart3 className="w-6 h-6 text-white/70" />
                    </motion.div>
                    <motion.div
                      animate={{ y: [-4, 4, -4] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="absolute top-10 left-6"
                    >
                      <Target className="w-5 h-5 text-white/50" />
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </section>
  );
}
